import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  BodyS,
  HeadlineS,
  LabelM,
} from '#src/atoms/typography/index.tsx';
import { QRCode } from '#src/components/QRCode/index.tsx';
import type {
  GraphQLResult,
  SubmitButtonProps,
} from '#src/components/form/Form/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { OTP_ATTEMPT_LENGTH } from '#src/constants/verificationCode.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useUpdate2FA } from '#src/hooks/useUpdate2FA.ts';

const messages = defineMessages({
  dialogSubtitle: {
    id: 'Settings.enable2FA.dialogSubtitle',
    defaultMessage: 'Setup instructions:',
  },
  dialogDescription1: {
    id: 'Settings.enable2FA.dialogDescription1',
    defaultMessage:
      '1. Download an authenticator app (e.g. Google Authenticator, 1Password, Authy)',
  },
  dialogDescription2: {
    id: 'Settings.enable2FA.dialogDescription2',
    defaultMessage: '2. Scan the QR code using your authenticator app',
  },
  dialogDescription2Subtitle: {
    id: 'Settings.enable2FA.dialogDescription2Subtitle',
    defaultMessage:
      'Or copy this key and enter it manually in your authenticator app',
  },
  dialogDescription3: {
    id: 'Settings.enable2FA.dialogDescription3',
    defaultMessage: '3. Enter the six-digit code',
  },
  submit: {
    id: 'Settings.enable2FA.submit',
    defaultMessage: 'Enable 2FA',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
`;

const QRCodeContainer = styled(Vertical).attrs({ center: true })`
  text-align: center;
  justify-content: flex-start;
`;

const DialogForm = styled(Vertical).attrs({ gap: 2, center: true })`
  justify-content: flex-start;
`;

const DialogContent = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
`;

type Props = {
  onEnabling2FA: (codes: string[]) => void;
};
export const Enable2FAForm = ({ onEnabling2FA }: Props) => {
  const { showNotification } = useSnackNotificationContext();

  const { enable2FAMutation, generateOtpProvisioningUriMutation } =
    useUpdate2FA();
  const [otpProvisioningUri, setOtpProvisioningUri] = useState<string | null>(
    null
  );

  useEffect(() => {
    generateOtpProvisioningUriMutation({
      variables: {
        input: {},
      },
    }).then(result => {
      if (result?.data?.generateOtpProvisioningUri) {
        setOtpProvisioningUri(
          result.data.generateOtpProvisioningUri.otpProvisioningUri
        );
      }
    });
  }, [generateOtpProvisioningUriMutation]);

  const otpCode = otpProvisioningUri
    ? new URL(otpProvisioningUri).searchParams.get('secret')
    : null;

  const submit = async (
    attributes: any,
    onResult: (result: GraphQLResult) => void
  ) => {
    const { data: mutationData } = await enable2FAMutation({
      variables: {
        input: { ...attributes },
      },
    });
    if (mutationData?.enable2Fa) {
      onResult(mutationData.enable2Fa);
    }
  };

  const onSuccess = (result: any) => {
    showNotification('2faEnabled');
    onEnabling2FA(result.otpBackupCodes);
  };

  return (
    <StyledGraphqlForm
      onSubmit={(attributes, doOnResult) => {
        submit(attributes, doOnResult);
      }}
      onChange={(values, doSubmit) => {
        const { otpAttempt } = values;
        if (otpAttempt?.length === OTP_ATTEMPT_LENGTH) doSubmit();
      }}
      onSuccess={onSuccess}
      render={(
        Error: React.ComponentType<React.PropsWithChildren<unknown>>,
        SubmitButton: FunctionComponent<
          React.PropsWithChildren<SubmitButtonProps>
        >
      ) => (
        <DialogContent>
          <HeadlineS as="h5">
            <FormattedMessage {...messages.dialogSubtitle} />
          </HeadlineS>
          <BodyM>
            <FormattedMessage {...messages.dialogDescription1} />
          </BodyM>
          <BodyM>
            <FormattedMessage {...messages.dialogDescription2} />
          </BodyM>
          <QRCodeContainer>
            <QRCode value={otpProvisioningUri!} />
            <BodyS bold>
              <FormattedMessage {...messages.dialogDescription2Subtitle} />
            </BodyS>
            <LabelM>{otpCode}</LabelM>
          </QRCodeContainer>
          <BodyM>
            <FormattedMessage {...messages.dialogDescription3} />
          </BodyM>
          <DialogForm>
            <div>
              <Error />
            </div>

            <TextField
              required
              name="otpAttempt"
              autoComplete="one-time-code"
              autoFocus
            />
            <SubmitButton size="medium">
              <FormattedMessage {...messages.submit} />
            </SubmitButton>
          </DialogForm>
        </DialogContent>
      )}
    />
  );
};
